import { Box, Button, Card, Flex, Icon, Modal, Typography, theme } from '@fe-toolkit/web-components/ssr'
import { useEffect, useState } from 'react'
import { useCopyToClipboard } from 'react-use'

import { customEvent } from '@fe-toolkit/eventing'

type ReferralOfferCardProps = {
  referralCode: string
}

export function ReferralOfferCard({ referralCode }: ReferralOfferCardProps) {
  const [showLearnMoreModal, setShowLearnMoreModal] = useState(false)
  const [copyState, copyToClipboard] = useCopyToClipboard()
  const [copied, setCopied] = useState(false)

  useEffect(() => {
    if (!copied) return

    const timeout = setTimeout(() => setCopied(false), 2000)

    return () => clearTimeout(timeout)
  }, [copied])

  const handleCopyReferralCode = () => {
    copyToClipboard(
      `Hey, join giffgaff broadband with my code and we'll each pocket a fancy £50 reward. Use ${referralCode} when you sign up at https://giffgaff.com/broadband`,
    )
    setCopied(true)
    customEvent('gg_click', { contextType: 'referral_code' })
  }

  return (
    <>
      <Card
        alignItems="center"
        backgroundColor="black"
        borderRadius="medium"
        display="flex"
        flexDirection="column"
        padding="small"
        sx={{
          flexShrink: 0,
          minHeight: '120px',
          scrollSnapAlign: 'start',
          width: '240px',
        }}
      >
        <Typography as="p" color="white" sx={{ fontSize: '20px', fontWeight: 'bold' }} textAlign="center">
          Invite a mate, get a £50 reward
        </Typography>
        <Flex alignItems="center" flexDirection="column" justifyContent="center" sx={{ flexGrow: 1, width: '100%' }}>
          <Flex sx={{ justifyContent: 'center', margin: '16px 0' }}>
            <Icon ariaHidden color="white" height="92px" iconKey="FilledTick" width="92px" />
          </Flex>
        </Flex>
        <Flex flexDirection="column" sx={{ gap: theme.spacing.tiny, width: '100%' }}>
          <Button
            hasUnderline
            isSmall
            sx={{ color: theme.colors.lightBlue }}
            textAlign="center"
            type="button"
            variant="link"
            onClick={() => setShowLearnMoreModal(true)}
          >
            How it works
          </Button>
          {copyState.error && (
            <Typography aria-live="polite" as="p" color="red" role="alert" textAlign="center" variant="whisper">
              Failed to copy code. Please try again.
            </Typography>
          )}
          <Button isFullWidth isSmall onClick={handleCopyReferralCode}>
            {copied ? 'Copied!' : 'Get referral code'}
          </Button>
        </Flex>
      </Card>
      <Modal
        backdrop
        closeModal={() => setShowLearnMoreModal(false)}
        display={showLearnMoreModal}
        modalBackgroundColour={'wildSand'}
        setDisplay={setShowLearnMoreModal}
      >
        <Flex alignItems="center" justifyContent="center" sx={{ marginBottom: theme.spacing.small }}>
          <Icon height="72px" iconKey="ShareTheLove" width="72px" />
        </Flex>

        <Typography as="h2" sx={{ marginBottom: theme.spacing.small }} textAlign="center" variant="loudBold">
          Invite a mate, get a fancy £50 reward
        </Typography>
        <Typography as="p" sx={{ marginBottom: theme.spacing.small }} textAlign="center" variant="speak">
          Send your code to a friend. They join with it. You both get a{' '}
          <Typography as="span" sx={{ fontWeight: 'bold' }}>
            £50
          </Typography>{' '}
          reward after they make their third payment. How good is that!?
        </Typography>

        {/* copy button */}
        <Button isFullWidth isSmall onClick={handleCopyReferralCode}>
          {copied ? 'Copied!' : 'Get referral code'}
        </Button>

        {/* terms link */}
        <Button
          isFullWidth
          ariaLabel="read our terms and conditions"
          hasExternalLink={true}
          href="https://www.giffgaff.com/terms/promotions#bb-referrals"
          sx={{
            position: 'absolute',
            bottom: '-7px',
            left: '0px',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            backgroundColor: 'white',
            padding: theme.spacing.medium,
            [theme.breakpoints.up('medium')]: {
              position: 'static',
              marginTop: theme.spacing.medium,
            },
          }}
          textAlign="center"
          variant="link"
        >
          <Typography
            sx={{
              color: theme.colors.linkBlue,
              textAlign: 'center',
              textDecoration: 'underline',
              marginRight: theme.spacing.small,
            }}
            variant="speakUp"
          >
            Terms and conditions
          </Typography>
          <Box aria-hidden="true" as="span" sx={{ marginTop: '7px' }}>
            <Icon color={theme.colors.linkBlue} height="24px" iconKey="Fullscreen" width="24px" />
          </Box>
        </Button>
      </Modal>
    </>
  )
}
